import React, { useState, useEffect, useCallback } from "react";
import { api, useBusiness, useAuth } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import UpgradePrompt from "@/components/UpgradePrompt";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(n || 0);

const GOAL_ICONS = {
  shield: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  building: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01M12 14h.01"/></svg>,
  trending_down: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>,
  sunset: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 18a5 5 0 00-10 0"/><line x1="12" y1="9" x2="12" y2="2"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/><line x1="23" y1="22" x2="1" y2="22"/><polyline points="8 6 12 2 16 6"/></svg>,
  target: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
};

const WealthEngine = () => {
  const { selectedBusiness } = useBusiness();
  const { subscription } = useAuth();
  const hasAccess = subscription?.is_active && subscription?.tier === "wealth_builder";
  const [deployData, setDeployData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [goalTypes, setGoalTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showContribute, setShowContribute] = useState(null);
  const [goalForm, setGoalForm] = useState({ name: "", goal_type: "", target_amount: "", current_amount: "0", deadline: "", notes: "" });
  const [contributeAmount, setContributeAmount] = useState("");
  const [contributeNotes, setContributeNotes] = useState("");

  const fetchAll = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const [deployRes, goalsRes, typesRes] = await Promise.all([
        api.get(`/wealth/${selectedBusiness.id}/deploy`),
        api.get(`/wealth/${selectedBusiness.id}/goals`),
        api.get("/wealth/goal-types"),
      ]);
      setDeployData(deployRes.data);
      setGoals(goalsRes.data);
      setGoalTypes(typesRes.data);
    } catch (error) {
      console.error("Failed to fetch wealth data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/wealth/${selectedBusiness.id}/goals`, {
        business_id: selectedBusiness.id,
        name: goalForm.name,
        goal_type: goalForm.goal_type,
        target_amount: parseFloat(goalForm.target_amount),
        current_amount: parseFloat(goalForm.current_amount) || 0,
        deadline: goalForm.deadline || null,
        notes: goalForm.notes || null,
      });
      toast.success("Goal created!");
      setShowAddGoal(false);
      setGoalForm({ name: "", goal_type: "", target_amount: "", current_amount: "0", deadline: "", notes: "" });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create goal");
    }
  };

  const handleContribute = async () => {
    const amount = parseFloat(contributeAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      const res = await api.post(`/wealth/goals/${showContribute.id}/contribute`, {
        amount, source: "manual", notes: contributeNotes || null,
      });
      toast.success(res.data.is_complete ? "Goal completed!" : `Contributed ${fmt(amount)}! Progress: ${res.data.progress}%`);
      setShowContribute(null);
      setContributeAmount("");
      setContributeNotes("");
      fetchAll();
    } catch (error) {
      toast.error("Failed to record contribution");
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await api.delete(`/wealth/goals/${goalId}`);
      toast.success("Goal removed");
      fetchAll();
    } catch (error) {
      toast.error("Failed to delete goal");
    }
  };

  if (!selectedBusiness) {
    return <div className="flex items-center justify-center min-h-[400px]"><p className="text-stone-600">Select a business first.</p></div>;
  }

  if (!hasAccess) {
    return <UpgradePrompt feature="Wealth Engine" requiredTier="Wealth Builder" />;
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><p className="text-stone-500">Loading wealth data...</p></div>;
  }

  const obligationData = deployData ? [
    { name: "Tax Gap", value: deployData.obligations.tax_gap, color: "#f59e0b" },
    { name: "Reserve Gap", value: deployData.obligations.reserve_gap, color: "#3b82f6" },
    { name: "Operating", value: deployData.obligations.operating_need, color: "#ef4444" },
    { name: "Deployable", value: deployData.available_to_deploy, color: "#10b981" },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6 pb-20" data-testid="wealth-engine-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">Wealth Engine</h1>
          <p className="text-stone-600 text-sm">Deploy your capital. Track your goals. Build wealth.</p>
        </div>
        <Button onClick={() => setShowAddGoal(true)} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="add-goal-btn">
          + New Goal
        </Button>
      </div>

      {/* Available to Deploy Hero */}
      {deployData && (
        <Card className="overflow-hidden" data-testid="deploy-card">
          <div className="h-2 bg-gradient-to-r from-[#1e3a5f] via-[#2d5a8e] to-emerald-500" />
          <CardContent className="pt-6 pb-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Deploy Amount */}
              <div className="lg:col-span-1">
                <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Available to Deploy</p>
                <p className="text-4xl font-bold text-[#1e3a5f] mb-2" data-testid="deploy-amount">{fmt(deployData.available_to_deploy)}</p>
                <p className="text-xs text-stone-500 mb-4">After covering all obligations from your {fmt(deployData.total_balance)} balance</p>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs"><span className="text-stone-500">Total Balance</span><span className="font-medium">{fmt(deployData.total_balance)}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-amber-600">Tax Gap</span><span className="font-medium text-amber-700">-{fmt(deployData.obligations.tax_gap)}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-blue-600">Reserve Gap</span><span className="font-medium text-blue-700">-{fmt(deployData.obligations.reserve_gap)}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-red-600">Operating Need</span><span className="font-medium text-red-700">-{fmt(deployData.obligations.operating_need)}</span></div>
                  <div className="h-px bg-stone-200 my-1" />
                  <div className="flex justify-between text-sm"><span className="font-semibold text-emerald-700">Deployable</span><span className="font-bold text-emerald-700">{fmt(deployData.available_to_deploy)}</span></div>
                </div>
              </div>

              {/* Donut Chart */}
              <div className="lg:col-span-1 flex items-center justify-center">
                {obligationData.length > 0 && (
                  <div className="w-48 h-48" data-testid="deploy-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={obligationData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" stroke="none">
                          {obligationData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => [fmt(v), undefined]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 -mt-2">
                      {obligationData.map(d => (
                        <div key={d.name} className="flex items-center gap-1"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} /><span className="text-[10px] text-stone-500">{d.name}</span></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Monthly Metrics */}
              <div className="lg:col-span-1">
                <p className="text-xs text-stone-500 uppercase tracking-wide mb-3">Monthly Snapshot</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[#1e3a5f]/5"><p className="text-[10px] text-[#1e3a5f]">Income</p><p className="text-lg font-bold text-[#1e3a5f]">{fmt(deployData.metrics.monthly_income)}</p></div>
                  <div className="p-3 rounded-lg bg-red-50"><p className="text-[10px] text-red-600">Expenses</p><p className="text-lg font-bold text-red-700">{fmt(deployData.metrics.monthly_expenses)}</p></div>
                  <div className="p-3 rounded-lg bg-emerald-50"><p className="text-[10px] text-emerald-600">Net Profit</p><p className="text-lg font-bold text-emerald-700">{fmt(deployData.metrics.monthly_net)}</p></div>
                  <div className="p-3 rounded-lg bg-violet-50"><p className="text-[10px] text-violet-600">Margin</p><p className="text-lg font-bold text-violet-700">{deployData.metrics.profit_margin}%</p></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="suggestions">
        <TabsList className="grid w-full grid-cols-2 max-w-sm">
          <TabsTrigger value="suggestions" data-testid="tab-suggestions">Suggestions</TabsTrigger>
          <TabsTrigger value="goals" data-testid="tab-goals">Goals ({goals.length})</TabsTrigger>
        </TabsList>

        {/* Suggestions Tab */}
        <TabsContent value="suggestions" className="space-y-4 mt-4">
          {deployData?.suggestions?.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-stone-500">No allocation suggestions right now. Your finances look well-balanced!</p></CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {deployData?.suggestions?.map((s) => (
                <Card key={s.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`suggestion-${s.id}`}>
                  <div className="h-1.5" style={{ backgroundColor: s.color }} />
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-sm text-stone-900">{s.label}</h4>
                        <Badge variant="outline" className="text-[10px] mt-1" style={{ color: s.color, borderColor: `${s.color}40` }}>
                          {s.type === 'obligation' ? 'Obligation' : s.type === 'safety' ? 'Safety Net' : s.type === 'debt' ? 'Debt' : 'Growth'}
                        </Badge>
                      </div>
                      <p className="text-lg font-bold" style={{ color: s.color }}>{fmt(s.amount)}</p>
                    </div>
                    <p className="text-xs text-stone-500 mb-3">{s.description}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-xs rounded-full"
                      style={{ borderColor: `${s.color}40`, color: s.color }}
                      onClick={() => {
                        setGoalForm({
                          name: s.label,
                          goal_type: s.id === "tax_reserve" ? "custom" : s.id === "retirement" ? "retirement" : s.id === "emergency_fund" ? "emergency_fund" : "custom",
                          target_amount: s.amount.toString(),
                          current_amount: "0",
                          deadline: "",
                          notes: s.description,
                        });
                        setShowAddGoal(true);
                      }}
                      data-testid={`suggestion-action-${s.id}`}
                    >
                      Create Goal from This
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Goals Tab */}
        <TabsContent value="goals" className="space-y-4 mt-4">
          {goals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="w-14 h-14 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-4">
                  {GOAL_ICONS.target}
                </div>
                <h3 className="font-semibold text-stone-900 mb-2">No Goals Yet</h3>
                <p className="text-stone-500 text-sm mb-4">Set your first financial goal to start tracking your wealth-building journey.</p>
                <Button onClick={() => setShowAddGoal(true)} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="add-first-goal-btn">Create Your First Goal</Button>
              </CardContent>
            </Card>
          ) : (
            goals.map((goal) => {
              const isComplete = goal.current_amount >= goal.target_amount;
              const remaining = Math.max(0, goal.target_amount - goal.current_amount);
              return (
                <Card key={goal.id} className={`overflow-hidden transition-all ${isComplete ? 'border-emerald-200 bg-emerald-50/30' : ''}`} data-testid={`goal-${goal.id}`}>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${goal.type_color}15`, color: goal.type_color }}>
                        {GOAL_ICONS[goal.type_icon] || GOAL_ICONS.target}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold text-stone-900">{goal.name}</h4>
                          {isComplete && <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">Complete</Badge>}
                        </div>
                        <p className="text-xs text-stone-500 mb-3">{goal.type_label}{goal.deadline ? ` · Target: ${new Date(goal.deadline).toLocaleDateString()}` : ''}</p>

                        <div className="flex items-end justify-between mb-2">
                          <div>
                            <span className="text-2xl font-bold" style={{ color: goal.type_color }}>{fmt(goal.current_amount)}</span>
                            <span className="text-sm text-stone-400 ml-1">/ {fmt(goal.target_amount)}</span>
                          </div>
                          <span className="text-sm font-semibold" style={{ color: goal.type_color }}>{goal.progress}%</span>
                        </div>
                        <Progress value={Math.min(100, goal.progress)} className="h-2.5 mb-3" />

                        {!isComplete && (
                          <p className="text-xs text-stone-400 mb-3">{fmt(remaining)} remaining</p>
                        )}

                        {goal.contributions?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-[10px] text-stone-400 mb-1">{goal.contributions.length} contribution{goal.contributions.length !== 1 ? 's' : ''}</p>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          {!isComplete && (
                            <Button size="sm" className="text-xs rounded-full" style={{ backgroundColor: goal.type_color }} onClick={() => setShowContribute(goal)} data-testid={`goal-contribute-${goal.id}`}>
                              + Contribute
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-xs text-red-500 hover:text-red-700" onClick={() => handleDeleteGoal(goal.id)} data-testid={`goal-delete-${goal.id}`}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>

      {/* Add Goal Dialog */}
      <Dialog open={showAddGoal} onOpenChange={setShowAddGoal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Financial Goal</DialogTitle>
            <DialogDescription>Set a target and track your progress over time.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateGoal} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Goal Type</Label>
              <Select value={goalForm.goal_type} onValueChange={(v) => {
                const meta = goalTypes[v];
                setGoalForm({ ...goalForm, goal_type: v, name: goalForm.name || (meta?.label || '') });
              }}>
                <SelectTrigger data-testid="goal-type-select"><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  {Object.entries(goalTypes).map(([key, meta]) => (
                    <SelectItem key={key} value={key}>{meta.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Goal Name</Label>
              <Input placeholder="e.g., Emergency Fund" value={goalForm.name} onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })} required data-testid="goal-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                  <Input type="number" min="1" step="0.01" className="pl-7" placeholder="50000" value={goalForm.target_amount} onChange={(e) => setGoalForm({ ...goalForm, target_amount: e.target.value })} required data-testid="goal-target-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Current Progress</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                  <Input type="number" min="0" step="0.01" className="pl-7" placeholder="0" value={goalForm.current_amount} onChange={(e) => setGoalForm({ ...goalForm, current_amount: e.target.value })} data-testid="goal-current-input" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Target Date (optional)</Label>
              <Input type="date" value={goalForm.deadline} onChange={(e) => setGoalForm({ ...goalForm, deadline: e.target.value })} data-testid="goal-deadline-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddGoal(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-[#1e3a5f] hover:bg-[#2d5a8e]" disabled={!goalForm.name || !goalForm.goal_type || !goalForm.target_amount} data-testid="goal-create-submit">Create Goal</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contribute Dialog */}
      <Dialog open={!!showContribute} onOpenChange={() => setShowContribute(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Contribute to {showContribute?.name}</DialogTitle>
            <DialogDescription>
              {showContribute && `${fmt(showContribute.current_amount)} of ${fmt(showContribute.target_amount)} (${showContribute.progress}%)`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                <Input type="number" min="1" step="0.01" className="pl-7" placeholder="1000" value={contributeAmount} onChange={(e) => setContributeAmount(e.target.value)} data-testid="contribute-amount-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input placeholder="e.g., Monthly savings transfer" value={contributeNotes} onChange={(e) => setContributeNotes(e.target.value)} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowContribute(null)}>Cancel</Button>
              <Button className="flex-1 bg-[#1e3a5f] hover:bg-[#2d5a8e]" onClick={handleContribute} disabled={!contributeAmount || parseFloat(contributeAmount) <= 0} data-testid="contribute-submit">Record Contribution</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WealthEngine;
